import { PaginatedFieldsContext, usePaginatedFields } from '@cibo/landmanager'
import { AuthUserPermission, Loading, usePermissions, useUser } from '@cibo/profile'
import { Markdown, ResponsiveDialog } from '@cibo/ui'
import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid, Stack } from '@mui/material'
import { GridRowId } from '@mui/x-data-grid-pro'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AgreementNotes } from '../AgreementNotes'
import { AgreementResetButton } from '../AgreementResetButton'
import { useAgreement, useAgreementCopy } from '../queries'
import { AgreementDetailProps } from '../types'
import { OfferAcceptButton } from './OfferAcceptButton'
import {
  OfferAgreementDeclinedFieldsTable,
  OfferAgreementFieldsTable,
} from './OfferAgreementFieldsTable'
import { DeclineDialogState, OfferDeclineDialog } from './OfferDeclineDialog'

export const OfferDetail = ({
  agreementId,
  setActionModal,
  downloadButton,
  markdownOverrides,
}: AgreementDetailProps) => {
  const { t } = useTranslation('impact/pages/Agreements/OfferDetail')
  const { can } = usePermissions()
  const canAcceptOffer = can({ useAny: AuthUserPermission.AGREEMENTS_OFFER_WRITE })
  const agreement = useAgreement(agreementId)
  const navigate = useNavigate()
  const user = useUser()
  const agreementCopy = useAgreementCopy(agreementId)
  const fieldSearch = usePaginatedFields(0, 1000, {
    agreementId: agreementId,
    includeAllResolved: true,
  })
  const { setSelectedRowIds } = useContext(PaginatedFieldsContext)

  const [acceptInfoDialogOpen, setAcceptInfoDialogOpen] = useState<boolean>(false)
  const [declineDialog, setDeclineDialog] = useState<DeclineDialogState>({
    open: false,
  })

  useEffect(() => {
    if (agreement.isFetched && fieldSearch.isFetched) {
      const eligibleWorkflowIds = fieldSearch.data?.items
        ?.map(field => field.id)
        .filter(item => item !== undefined)
      setSelectedRowIds(eligibleWorkflowIds as GridRowId[])
    }
  }, [agreement.isFetched, fieldSearch.isFetched])

  if (agreement.isLoading || user.isLoading) {
    return <Loading />
  }

  const closeAcceptDialog = () => {
    setAcceptInfoDialogOpen(false)
    navigate('../')
  }
  const onSuccess = (agreeFields: boolean) => {
    if (agreeFields) {
      setAcceptInfoDialogOpen(true)
    } else {
      navigate('../')
    }
  }

  const ownsAgreement = agreement.data && user.data?.matchUserId(agreement.data.userId)
  const showAcceptButton =
    canAcceptOffer && agreement.data?.status === 'pending' && agreement.data?.isAllowed('can_agree')

  return (
    <Box>
      <Grid container spacing={6}>
        <Grid item lg={9} md={8} xs={12}>
          <Stack spacing={2}>
            {agreementCopy && <Markdown overrides={markdownOverrides}>{agreementCopy}</Markdown>}
            <Stack spacing={4}>
              {/* The below is in the interest of having an empty table if there are no declined fields on a declined offer. */}
              {(agreement.data?.status !== 'declined' ||
                !agreement.data?.declined ||
                agreement.data.declined.length === 0) && (
                <OfferAgreementFieldsTable agreementId={agreementId} />
              )}
              {agreement.data?.declined && agreement.data.declined.length > 0 && (
                <OfferAgreementDeclinedFieldsTable declined={agreement.data?.declined} />
              )}
            </Stack>
          </Stack>
        </Grid>{' '}
        <Grid item lg={3} md={4} xs={12}>
          <Stack spacing={2}>
            {showAcceptButton && (
              <OfferAcceptButton
                numItems={fieldSearch.data?.numItems}
                setDeclineDialog={setDeclineDialog}
                setAcceptInfoDialogOpen={setAcceptInfoDialogOpen}
                agreementId={agreementId}
              />
            )}
            <Stack
              spacing={1}
              padding={showAcceptButton ? 2 : 0}
              alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            >
              <Box>{downloadButton}</Box>
              <AgreementResetButton
                agreementId={agreementId}
                ownsAgreement={ownsAgreement}
                setActionModal={setActionModal}
              />
            </Stack>
            <AgreementNotes agreementId={agreementId} />
          </Stack>
        </Grid>
      </Grid>

      <OfferDeclineDialog
        agreementId={agreementId}
        declineDialogState={declineDialog}
        setDeclineDialogState={setDeclineDialog}
        onSuccess={onSuccess}
      />

      <ResponsiveDialog open={acceptInfoDialogOpen} onClose={closeAcceptDialog}>
        <DialogTitle>{t('congratulations')}</DialogTitle>
        <DialogContent>
          <Markdown overrides={markdownOverrides}>{agreementCopy}</Markdown>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAcceptDialog}>{t('okay')}</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  )
}
